import React, { FC, memo, useCallback, useMemo } from 'react';
import { getFeatureFlags } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Box } from 'entities/Box';
import { useGetSizeCodesForBookingQuery } from 'entities/SizeCode';
import { BoxSizeFilter } from 'features/BoxSizeFilter';
import { requestFormModalActions, RequestModalMode } from 'widgets/RequestModal';
import { SizeCodeCollapse } from 'features/SizeCodeCollapse';
import { SizeGuideModal, sizeGuideModalActions } from 'features/SizeGuideModal';
import { Button } from 'shared/ui/Button';
import { useGetRentOptionsQuery } from 'entities/RentOption';
import { WarehouseForBooking } from 'entities/Warehouse';
import { UserAccountType } from 'entities/User';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { WarehouseMap } from './WarehouseMap';
import { WarehouseMapMobile } from './WarehouseMapMobile';

interface SelectBoxSizeContentProps {
  selectedBoxId: string | undefined;
  selectedWarehouseId: string | undefined;
  viewMode: 'list' | 'map';
  isMapAvailable: boolean;
  onSelectBox: (box: Box | undefined, sizeCodeId?: string) => void;
  onCompleteStep: (box: Box, sizeCodeId: string) => void;
  onSwitchToList?: () => void;
  warehouseInfo?: Nullable<WarehouseForBooking>;
  accountType?: Nullable<UserAccountType>;
  isShowPromotionPriceNeeded?: boolean;
}

export const SelectBoxSizeContent: FC<SelectBoxSizeContentProps> = memo((props) => {
  const {
    selectedBoxId,
    selectedWarehouseId,
    onCompleteStep,
    onSelectBox,
    onSwitchToList,
    viewMode,
    isMapAvailable,
    warehouseInfo,
    accountType,
    isShowPromotionPriceNeeded,
  } = props;

  const { t } = useAppTranslation('booking');
  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(getFeatureFlags);
  const isBookingMapEnabled = Boolean(featureFlags?.bookingMap);

  const { isDesktop } = useScreenBreakpoints();
  const isBookingMapAvailable = isBookingMapEnabled && isMapAvailable;
  const isMobileMapMode = !isDesktop && viewMode === 'map' && isBookingMapAvailable;

  const { data: sizeCodes } = useGetSizeCodesForBookingQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });
  const { data: rentOptions } = useGetRentOptionsQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });

  const sortedSizeCodes = useMemo(
    () =>
      [...(sizeCodes || [])].sort((sizeCode1, sizeCode2) => {
        if (sizeCode1.sizeCodeType === 'M3' && sizeCode2.sizeCodeType !== 'M3') {
          return -1;
        }

        if (sizeCode1.sizeCodeType !== 'M3' && sizeCode2.sizeCodeType === 'M3') {
          return 1;
        }

        return 0;
      }),
    [sizeCodes],
  );

  const handleOpenRequestFormModal = useCallback((): void => {
    dispatch(
      requestFormModalActions.openRequestModal({
        title: t('Let`s get to know each other better!'),
        description: t('Please provide us with your information so that we can suggest the best pricing options that meet your needs.'),
        mode: RequestModalMode.REQUEST_OFFER,
      }),
    );
  }, [dispatch, t]);

  const openSizeGuideModal = useCallback((): void => {
    dispatch(sizeGuideModalActions.setOpenModal(true));
  }, [dispatch]);

  return (
    <div className="bg-secondaryLight rounded-lg">
      <BoxSizeFilter
        sizeCodes={sortedSizeCodes}
        headerAction={
          <Button theme="clear" fontSize="large" onClick={openSizeGuideModal}>
            {t('Size Guide')}
          </Button>
        }
      />
      {viewMode === 'map' && isBookingMapAvailable && isDesktop ? (
        <WarehouseMap
          selectedWarehouseId={selectedWarehouseId}
          selectedBoxId={selectedBoxId}
          rentOptions={rentOptions}
          onSelectBox={onSelectBox}
          onBookBox={onCompleteStep}
          warehouseInfo={warehouseInfo ?? undefined}
          accountType={accountType}
          isShowPromotionPriceNeeded={Boolean(isShowPromotionPriceNeeded)}
        />
      ) : !isMobileMapMode ? (
        <SizeCodeCollapse
          sizeCodes={sortedSizeCodes}
          rentOptions={rentOptions}
          selectedBoxId={selectedBoxId}
          onSelectBox={onSelectBox}
          onBookBox={onCompleteStep}
        />
      ) : null}
      {isBookingMapAvailable ? (
        <WarehouseMapMobile
          isOpen={isMobileMapMode}
          onClose={onSwitchToList}
          onSwitchToList={onSwitchToList}
          selectedWarehouseId={selectedWarehouseId}
          selectedBoxId={selectedBoxId}
          rentOptions={rentOptions}
          onSelectBox={onSelectBox}
          onBookBox={onCompleteStep}
          warehouseInfo={warehouseInfo ?? undefined}
          accountType={accountType}
          isShowPromotionPriceNeeded={Boolean(isShowPromotionPriceNeeded)}
        />
      ) : null}
      <SizeGuideModal sizeCodes={sortedSizeCodes} />
      <div className="mt-4 flex justify-center flex-wrap">
        <div className="flex items-center justify-end flex-col">
          <div>{t('Did not find what you were looking for?')}</div>
          <Button theme="clear" className="underline" fontSize="large" onClick={handleOpenRequestFormModal}>
            {t('Request an offer')}
          </Button>
        </div>
      </div>
    </div>
  );
});
